import { devLog, devError } from '../utils/devLogger';
/**
 * QA Service
 *
 * Handles quality assurance operations including:
 * - Document review (approve/reject)
 * - Flagging documents for review
 * - Generating QA reports
 * - Tracking review metrics
 */

import { db, storage } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  documentId
} from 'firebase/firestore';
import { ref, getDownloadURL, getMetadata } from 'firebase/storage';

class QAService {
  constructor() {
    this.documentsCollection = 'documents';
    this.qaReviewsCollection = 'qaReviews';
    this.auditCollection = 'documentValidationAudit';
    this._documentsCache = null;
    this._documentsCacheAt = 0;
    this._documentsCacheKey = '';
  }

  _resolveDocumentCategoryKey(data = {}) {
    const candidates = [
      data.categoryKey,
      data.categoryId,
      data.category,
      data.type,
      data.documentType
    ].filter(Boolean);

    for (const candidate of candidates) {
      const normalized = String(candidate).trim();
      if (!normalized) continue;
      return normalized.toLowerCase();
    }
    return 'uncategorized';
  }

  _isFinalReviewStatus(item) {
    const reviewStatus = String(item?.reviewStatus || '').toLowerCase();
    const status = String(item?.status || '').toLowerCase();
    return ['qa_approved', 'qa_rejected'].includes(reviewStatus) || ['verified', 'rejected', 'approved'].includes(status);
  }

  _isStaleUnreviewedDocument(item, days = 30) {
    if (!item || item.deleted === true) return false;
    if (this._isFinalReviewStatus(item)) return false;
    const uploadedAt = item.uploadedAt instanceof Date ? item.uploadedAt : new Date(item.uploadedAt || 0);
    if (Number.isNaN(uploadedAt.getTime())) return false;
    const ageMs = Date.now() - uploadedAt.getTime();
    const ageDays = ageMs / (1000 * 60 * 60 * 24);
    return ageDays > days;
  }

  async _autoCleanupStaleUnreviewedDocuments(documents = []) {
    const staleDocs = documents.filter((item) => this._isStaleUnreviewedDocument(item, 30));
    if (!staleDocs.length) return documents;

    devLog(`🧹 QA Service: Auto-cleaning ${staleDocs.length} stale unreviewed document(s) (>30 days)`);

    const chunkSize = 50;
    for (let i = 0; i < staleDocs.length; i += chunkSize) {
      const chunk = staleDocs.slice(i, i + chunkSize);
      // eslint-disable-next-line no-await-in-loop
      await Promise.allSettled(
        chunk.map(async (item) => {
          try {
            const targetRef = item?.docPath ? doc(db, item.docPath) : doc(db, this.documentsCollection, item.id);
            await updateDoc(targetRef, {
              deleted: true,
              deletedAt: serverTimestamp(),
              deletedBy: 'system_auto_cleanup',
              cleanupReason: 'stale_unreviewed_over_30_days'
            });
            item.deleted = true;
            item.cleanupReason = 'stale_unreviewed_over_30_days';
          } catch (cleanupError) {
            devLog('QA Service: stale document cleanup skipped', item?.id, cleanupError?.message || cleanupError);
          }
        })
      );
    }

    return documents.filter((item) => item?.deleted !== true);
  }

  _collapseDuplicateDocuments(documents = []) {
    // Keep only the latest visible document per learner+category in QA listings.
    const map = new Map();

    documents.forEach((item) => {
      if (!item || item.deleted === true) return;

      const learnerId = item.userInfo?.id || item.userId || item.learnerId || 'unknown';
      const categoryKey = this._resolveDocumentCategoryKey(item);
      const key = `${learnerId}__${categoryKey}`;

      const existing = map.get(key);
      if (!existing) {
        map.set(key, item);
        return;
      }

      const existingDate = existing.uploadedAt instanceof Date ? existing.uploadedAt : new Date(existing.uploadedAt || 0);
      const currentDate = item.uploadedAt instanceof Date ? item.uploadedAt : new Date(item.uploadedAt || 0);
      if (currentDate >= existingDate) {
        map.set(key, item);
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      const dateA = a.uploadedAt instanceof Date ? a.uploadedAt : new Date(a.uploadedAt || 0);
      const dateB = b.uploadedAt instanceof Date ? b.uploadedAt : new Date(b.uploadedAt || 0);
      return dateB - dateA;
    });
  }

  _extractUserIdFromPath(path = '') {
    const parts = String(path || '').split('/');
    if (parts.length >= 4 && parts[0] === 'users' && parts[2] === 'documents') {
      return parts[1];
    }
    return null;
  }

  async _syncLearnerDocumentMirrorStatus(sourceDocRef, sourceData, updatePayload) {
    try {
      const sourcePath = sourceDocRef?.path || '';
      const sourceId = sourceDocRef?.id || null;
      const userId = sourceData?.userId || this._extractUserIdFromPath(sourcePath);
      if (!userId) return;

      // If QA reviewed a learner subcollection document, learner-facing status is already updated.
      if (sourcePath.startsWith(`users/${userId}/documents/`)) {
        return;
      }

      const userDocsRef = collection(db, 'users', userId, 'documents');

      // 1) Try exact ID mirror first.
      const exactRef = sourceId ? doc(db, 'users', userId, 'documents', sourceId) : null;
      if (exactRef) {
        const exactSnap = await getDoc(exactRef);
        if (exactSnap.exists()) {
          await updateDoc(exactRef, updatePayload);
          return;
        }
      }

      // 2) Try finding the specific learner doc by stable file identifiers.
      const candidates = [];
      const tryQuery = async (field, value) => {
        if (!value) return;
        try {
          const snap = await getDocs(query(userDocsRef, where(field, '==', value), limit(10)));
          snap.docs.forEach((d) => candidates.push(d));
        } catch (err) {
          devLog(`QA Service: mirror sync lookup failed for ${field}`, err?.message || err);
        }
      };

      await tryQuery('storagePath', sourceData?.storagePath || sourceData?.filePath || null);
      await tryQuery('fileURL', sourceData?.fileURL || sourceData?.downloadURL || null);
      await tryQuery('categoryId', sourceData?.categoryId || null);
      await tryQuery('categoryKey', sourceData?.categoryKey || null);
      await tryQuery('category', sourceData?.category || null);

      if (!candidates.length) return;

      const uniqueCandidates = Array.from(new Map(candidates.map((d) => [d.ref.path, d])).values());

      // Prefer exact file match; otherwise update the latest doc in the same category.
      const sourceStoragePath = sourceData?.storagePath || sourceData?.filePath || null;
      const sourceFileURL = sourceData?.fileURL || sourceData?.downloadURL || null;

      const scored = uniqueCandidates.map((docSnap) => {
        const data = docSnap.data();
        let score = 0;
        if (sourceStoragePath && (data.storagePath === sourceStoragePath || data.filePath === sourceStoragePath)) score += 100;
        if (sourceFileURL && (data.fileURL === sourceFileURL || data.downloadURL === sourceFileURL)) score += 90;
        if (sourceData?.categoryId && data.categoryId === sourceData.categoryId) score += 10;
        if (sourceData?.categoryKey && data.categoryKey === sourceData.categoryKey) score += 8;
        if (sourceData?.category && data.category === sourceData.category) score += 6;
        const uploadedAt = data.uploadedAt?.toDate?.() || new Date(data.uploadedAt || 0);
        return { docSnap, score, uploadedAt };
      });

      scored.sort((a, b) => (b.score - a.score) || ((b.uploadedAt?.getTime?.() || 0) - (a.uploadedAt?.getTime?.() || 0)));
      const best = scored[0]?.docSnap;
      if (best) {
        await updateDoc(best.ref, updatePayload);
      }
    } catch (error) {
      devLog('QA Service: learner document mirror status sync skipped', error?.message || error);
    }
  }

  /**
   * Get all documents for QA review
   * Uses a two-step approach: first get all learners, then get their documents
   * This avoids collectionGroup permission issues
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} List of documents
   */
  async getAllDocuments(filters = {}) {
    try {
      const cacheKey = JSON.stringify(filters || {});
      const now = Date.now();
      if (this._documentsCache && this._documentsCacheKey === cacheKey && (now - this._documentsCacheAt) < 60000) {
        return this._documentsCache;
      }

      devLog('🔍 QA Service: Fetching documents for QA review...');

      // Fast path: try centralized documents collection first
      const centralDocs = await this.getDocumentsFromCentralCollection(filters).catch(() => []);
      if (centralDocs && centralDocs.length > 0) {
        const cleanedCentralDocs = await this._autoCleanupStaleUnreviewedDocuments(centralDocs);
        const collapsedCentralDocs = this._collapseDuplicateDocuments(cleanedCentralDocs);
        this._documentsCache = collapsedCentralDocs;
        this._documentsCacheAt = Date.now();
        this._documentsCacheKey = cacheKey;
        return collapsedCentralDocs;
      }

      // Step 1: Get all learner users
      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, where('role', '==', 'learner'));
      const usersSnapshot = await getDocs(usersQuery);

      devLog(`📊 Found ${usersSnapshot.size} learners`);

      // Step 2: Get all programs for reference
      const programsRef = collection(db, 'programs');
      const programsSnapshot = await getDocs(programsRef);
      const programsMap = {};
      programsSnapshot.docs.forEach(programDoc => {
        programsMap[programDoc.id] = {
          id: programDoc.id,
          name: programDoc.data().name,
          ...programDoc.data()
        };
      });
      devLog(`📚 Found ${Object.keys(programsMap).length} programs`);

      const allDocuments = [];

      // Step 3: Fetch learner documents in parallel batches to reduce load time
      const userDocs = usersSnapshot.docs;
      const chunkSize = 10;
      for (let i = 0; i < userDocs.length; i += chunkSize) {
        const chunk = userDocs.slice(i, i + chunkSize);
        const chunkResults = await Promise.all(
          chunk.map(async (userDoc) => {
            const userData = userDoc.data();
            const userId = userDoc.id;

            try {
              // Get documents from user's subcollection
              const userDocsRef = collection(db, 'users', userId, 'documents');
              let userDocsQuery = userDocsRef;

              // Apply filters if provided
              const constraints = [];

              if (filters.reviewStatus) {
                constraints.push(where('reviewStatus', '==', filters.reviewStatus));
              }

              if (filters.validationStatus) {
                constraints.push(where('validationStatus', '==', filters.validationStatus));
              }

              if (filters.category) {
                constraints.push(where('category', '==', filters.category));
              }

              if (filters.flagged) {
                constraints.push(where('flaggedForReview', '==', true));
              }

              if (constraints.length > 0) {
                userDocsQuery = query(userDocsRef, ...constraints);
              }

              const docsSnapshot = await getDocs(userDocsQuery);
              const docsForUser = [];

              for (const docSnap of docsSnapshot.docs) {
                const data = docSnap.data();
                const programId = userData.programId || 'unassigned';
                const programInfo = programsMap[programId] || { id: 'unassigned', name: 'Unassigned' };
                const normalizedReviewStatus =
                  data.reviewStatus ||
                  (data.status === 'verified' ? 'qa_approved' : data.status === 'rejected' ? 'qa_rejected' : undefined);
                const normalizedStatus =
                  normalizedReviewStatus === 'qa_approved'
                    ? 'verified'
                    : normalizedReviewStatus === 'qa_rejected'
                      ? 'rejected'
                      : (data.status || 'pending');

                docsForUser.push({
                  id: docSnap.id,
                  docPath: docSnap.ref.path,
                  ...data,
                  status: normalizedStatus,
                  reviewStatus: normalizedReviewStatus || data.reviewStatus,
                  userId: userId,
                  programId: programId,
                  programName: programInfo.name,
                  programInfo: programInfo,
                  userInfo: {
                    id: userId,
                    displayName: userData.displayName || userData.email,
                    email: userData.email,
                    role: userData.role,
                    programId: programId,
                    cohortId: userData.cohortId || ''
                  },
                  uploadedAt: data.uploadedAt?.toDate?.() || new Date(data.uploadedAt || Date.now())
                });
              }

              return docsForUser;
            } catch (userError) {
              // Skip users where we can't access documents (might not have any)
              devLog(`Could not fetch documents for user ${userId}:`, userError.message);
              return [];
            }
          })
        );

        chunkResults.flat().forEach((doc) => allDocuments.push(doc));
      }

      // Sort by uploadedAt descending
      allDocuments.sort((a, b) => {
        const dateA = a.uploadedAt instanceof Date ? a.uploadedAt : new Date(a.uploadedAt);
        const dateB = b.uploadedAt instanceof Date ? b.uploadedAt : new Date(b.uploadedAt);
        return dateB - dateA;
      });

      devLog(`✅ QA Service: Found ${allDocuments.length} total documents`);

      const cleanedDocuments = await this._autoCleanupStaleUnreviewedDocuments(allDocuments);
      const collapsedDocuments = this._collapseDuplicateDocuments(cleanedDocuments);
      this._documentsCache = collapsedDocuments;
      this._documentsCacheAt = Date.now();
      this._documentsCacheKey = cacheKey;

      return collapsedDocuments;
    } catch (error) {
      devError('Error fetching documents:', error);
      throw new Error('Failed to fetch documents: ' + error.message);
    }
  }

  async getDocumentsFromCentralCollection(filters = {}) {
    const documentsRef = collection(db, this.documentsCollection);
    const constraints = [];

    if (filters.reviewStatus) {
      constraints.push(where('reviewStatus', '==', filters.reviewStatus));
    }
    if (filters.validationStatus) {
      constraints.push(where('validationStatus', '==', filters.validationStatus));
    }
    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters.flagged) {
      constraints.push(where('flaggedForReview', '==', true));
    }

    const docsQuery = constraints.length > 0 ? query(documentsRef, ...constraints) : documentsRef;
    const docsSnapshot = await getDocs(docsQuery);

    if (docsSnapshot.empty) {
      return [];
    }

    const userIds = Array.from(
      new Set(
        docsSnapshot.docs
          .map(docSnap => docSnap.data()?.userId)
          .filter(Boolean)
      )
    );

    const userMap = {};
    for (let i = 0; i < userIds.length; i += 10) {
      const chunk = userIds.slice(i, i + 10);
      const usersSnapshot = await getDocs(
        query(collection(db, 'users'), where(documentId(), 'in', chunk))
      );
      usersSnapshot.docs.forEach(docSnap => {
        userMap[docSnap.id] = docSnap.data();
      });
    }

    const programsSnapshot = await getDocs(collection(db, 'programs'));
    const programsMap = {};
    programsSnapshot.docs.forEach(programDoc => {
      programsMap[programDoc.id] = {
        id: programDoc.id,
        name: programDoc.data().name,
        ...programDoc.data()
      };
    });

    const allDocuments = docsSnapshot.docs.map(docSnap => {
      const data = docSnap.data();
      const userData = userMap[data.userId] || {};
      const programId = userData.programId || data.programId || 'unassigned';
      const programInfo = programsMap[programId] || { id: 'unassigned', name: 'Unassigned' };
      const normalizedReviewStatus =
        data.reviewStatus ||
        (data.status === 'verified' ? 'qa_approved' : data.status === 'rejected' ? 'qa_rejected' : undefined);
      const normalizedStatus =
        normalizedReviewStatus === 'qa_approved'
          ? 'verified'
          : normalizedReviewStatus === 'qa_rejected'
            ? 'rejected'
            : (data.status || 'pending');

      return {
        id: docSnap.id,
        docPath: docSnap.ref.path,
        ...data,
        status: normalizedStatus,
        reviewStatus: normalizedReviewStatus || data.reviewStatus,
        userId: data.userId,
        programId: programId,
        programName: programInfo.name,
        programInfo: programInfo,
        userInfo: {
          id: data.userId,
          displayName: userData.displayName || userData.email,
          email: userData.email,
          role: userData.role,
          programId: programId,
          cohortId: userData.cohortId || ''
        },
        uploadedAt: data.uploadedAt?.toDate?.() || new Date(data.uploadedAt || Date.now())
      };
    });

    allDocuments.sort((a, b) => {
      const dateA = a.uploadedAt instanceof Date ? a.uploadedAt : new Date(a.uploadedAt);
      const dateB = b.uploadedAt instanceof Date ? b.uploadedAt : new Date(b.uploadedAt);
      return dateB - dateA;
    });

    const cleanedDocuments = await this._autoCleanupStaleUnreviewedDocuments(allDocuments);
    return this._collapseDuplicateDocuments(cleanedDocuments);
  }

  /**
   * Get all documents grouped by program
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Documents grouped by program
   */
  async getDocumentsGroupedByProgram(filters = {}) {
    try {
      const allDocuments = await this.getAllDocuments(filters);

      // Group by program
      const groupedByProgram = {};

      allDocuments.forEach(doc => {
        const programId = doc.programId || 'unassigned';
        const programName = doc.programName || 'Unassigned';

        if (!groupedByProgram[programId]) {
          groupedByProgram[programId] = {
            programId: programId,
            programName: programName,
            programInfo: doc.programInfo || { id: programId, name: programName },
            documents: [],
            stats: {
              total: 0,
              pending: 0,
              verified: 0,
              rejected: 0
            }
          };
        }

        groupedByProgram[programId].documents.push(doc);
        groupedByProgram[programId].stats.total++;

        // Count by status
        const status = doc.reviewStatus || doc.status || 'pending';
        if (status === 'verified' || status === 'approved' || status === 'qa_approved') {
          groupedByProgram[programId].stats.verified++;
        } else if (status === 'rejected' || status === 'qa_rejected') {
          groupedByProgram[programId].stats.rejected++;
        } else {
          groupedByProgram[programId].stats.pending++;
        }
      });

      devLog(`📊 Documents grouped into ${Object.keys(groupedByProgram).length} programs`);

      return {
        grouped: groupedByProgram,
        totalDocuments: allDocuments.length,
        programs: Object.keys(groupedByProgram).map(id => ({
          id,
          name: groupedByProgram[id].programName,
          count: groupedByProgram[id].documents.length
        }))
      };
    } catch (error) {
      devError('Error grouping documents by program:', error);
      throw new Error('Failed to group documents: ' + error.message);
    }
  }

  /**
   * Get flagged documents (pending QA review)
   * @returns {Promise<Array>} List of flagged documents
   */
  async getFlaggedDocuments() {
    return this.getAllDocuments({
      flagged: true,
      reviewStatus: 'pending_review'
    });
  }

  /**
   * Get document by ID or path with full details
   * @param {string} documentId - Document ID or full path (e.g., "users/userId/documents/docId")
   * @returns {Promise<Object>} Document details
   */
  async getDocumentById(documentId) {
    try {
      // Check if documentId is a full path (contains slashes) or just an ID
      let docRef;
      if (documentId.includes('/')) {
        // It's a full path like "users/userId/documents/docId"
        docRef = doc(db, documentId);
      } else {
        // It's just an ID, use old behavior for top-level documents collection
        docRef = doc(db, this.documentsCollection, documentId);
      }

      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Document not found');
      }

      const data = docSnap.data();

      // Get file info from various possible field locations
      // Documents can have different structures depending on how they were uploaded
      let primaryFile = Array.isArray(data.files) && data.files.length > 0 ? data.files[0] : null;

      // Get download URL - check multiple possible field names
      let downloadURL = data.downloadURL || data.fileURL || primaryFile?.downloadURL || null;

      // Get storage path - check multiple possible field names
      let storagePath = data.storagePath || data.filePath || primaryFile?.storagePath || null;

      // Fallback: if central doc is missing file info, try to resolve from learner subcollection
      if ((!downloadURL || !storagePath) && data.userId) {
        try {
          const userDocsRef = collection(db, 'users', data.userId, 'documents');
          let fallbackQuery = null;
          // Avoid orderBy to prevent composite index requirement.
          if (data.categoryId) {
            fallbackQuery = query(userDocsRef, where('categoryId', '==', data.categoryId), limit(1));
          } else if (data.categoryKey) {
            fallbackQuery = query(userDocsRef, where('categoryKey', '==', data.categoryKey), limit(1));
          } else if (data.category) {
            fallbackQuery = query(userDocsRef, where('category', '==', data.category), limit(1));
          }

          if (fallbackQuery) {
            const fallbackSnap = await getDocs(fallbackQuery);
            if (!fallbackSnap.empty) {
              const fallbackDoc = fallbackSnap.docs[0];
              const fallbackData = fallbackDoc.data();
              primaryFile = Array.isArray(fallbackData.files) && fallbackData.files.length > 0
                ? fallbackData.files[0]
                : primaryFile;
              downloadURL = downloadURL || fallbackData.downloadURL || fallbackData.fileURL || primaryFile?.downloadURL || null;
              storagePath = storagePath || fallbackData.storagePath || fallbackData.filePath || primaryFile?.storagePath || null;
            }
          }
        } catch (fallbackError) {
          devLog('QA Service: Unable to resolve fallback document data:', fallbackError);
        }
      }

      // If no downloadURL found, try to get it from storage path
      if (!downloadURL && storagePath) {
        try {
          const storageRef = ref(storage, storagePath);
          downloadURL = await getDownloadURL(storageRef);
        } catch (error) {
          devError('Error getting download URL from storage:', error);
        }
      }

      // Get file size and type from various possible locations
      let fileSize = data.fileSize || data.size || primaryFile?.size || null;
      let fileType = data.fileType || data.type || primaryFile?.type || null;
      let fileName = data.fileName || primaryFile?.name || 'Unknown';

      // If we have a storage path but still missing metadata, fetch it from Storage
      if (storagePath && (!fileSize || !fileType || !fileName || fileName === 'Unknown')) {
        try {
          const storageRef = ref(storage, storagePath);
          const metadata = await getMetadata(storageRef);
          fileSize = fileSize || metadata.size || null;
          fileType = fileType || metadata.contentType || null;
          fileName = (fileName && fileName !== 'Unknown') ? fileName : metadata.name || fileName;
        } catch (metadataError) {
          devLog('QA Service: Unable to fetch storage metadata:', metadataError);
        }
      }

      devLog('📄 Document details:', {
        id: docSnap.id,
        fileName: fileName,
        hasDownloadURL: !!downloadURL,
        downloadURL: downloadURL ? downloadURL.substring(0, 100) + '...' : 'null',
        hasStoragePath: !!storagePath,
        fileSize: fileSize,
        fileType: fileType
      });

      // Get user info
      let userInfo = null;
      if (data.userId) {
        const userDoc = await getDoc(doc(db, 'users', data.userId));
        if (userDoc.exists()) {
          userInfo = {
            id: userDoc.id,
            ...userDoc.data()
          };
        }
      }

      // Get review history
      const reviewHistory = await this.getDocumentReviewHistory(documentId);

      return {
        id: docSnap.id,
        ...data,
        downloadURL,
        fileSize,
        fileType,
        fileName,
        storagePath,
        userInfo,
        reviewHistory,
        uploadedAt: data.uploadedAt?.toDate?.() || new Date(data.uploadedAt || Date.now())
      };
    } catch (error) {
      devError('Error fetching document:', error);
      throw new Error('Failed to fetch document: ' + error.message);
    }
  }

  /**
   * Flag a document for QA review
   * @param {string} documentId - Document ID
   * @param {string} reason - Reason for flagging
   * @param {string} flaggedBy - User ID who flagged
   * @returns {Promise<Object>} Result
   */
  async flagDocumentForReview(documentId, reason, flaggedBy) {
    try {
      // Handle both full path and document ID
      let docRef;
      if (documentId.includes('/')) {
        docRef = doc(db, documentId);
      } else {
        docRef = doc(db, this.documentsCollection, documentId);
      }

      await updateDoc(docRef, {
        flaggedForReview: true,
        flaggedReason: reason,
        flaggedBy: flaggedBy,
        flaggedAt: serverTimestamp(),
        reviewStatus: 'pending_review'
      });

      // Log to audit trail
      await addDoc(collection(db, this.auditCollection), {
        documentId,
        action: 'flagged_for_review',
        reason,
        performedBy: flaggedBy,
        timestamp: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      devError('Error flagging document:', error);
      throw new Error('Failed to flag document: ' + error.message);
    }
  }

  /**
   * Approve a document
   * @param {string} documentId - Document ID
   * @param {string} reviewerId - QA reviewer user ID
   * @param {string} notes - Review notes
   * @returns {Promise<Object>} Result
   */
  async approveDocument(documentId, reviewerId, notes = '') {
    try {
      // Handle both full path and document ID
      let docRef;
      if (documentId.includes('/')) {
        docRef = doc(db, documentId);
      } else {
        docRef = doc(db, this.documentsCollection, documentId);
      }

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('Document not found');
      }
      const docData = docSnap.data();

      const reviewUpdate = {
        reviewStatus: 'qa_approved',
        status: 'verified',
        reviewedBy: reviewerId,
        reviewedAt: serverTimestamp(),
        reviewNotes: notes,
        flaggedForReview: false
      };

      // Update document status
      await updateDoc(docRef, reviewUpdate);
      await this._syncLearnerDocumentMirrorStatus(docRef, docData, reviewUpdate);

      // Create review record
      await addDoc(collection(db, this.qaReviewsCollection), {
        documentId,
        reviewerId,
        action: 'approved',
        notes,
        timestamp: serverTimestamp()
      });

      // Log to audit trail
      await addDoc(collection(db, this.auditCollection), {
        documentId,
        action: 'qa_approved',
        reviewedBy: reviewerId,
        notes,
        timestamp: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      devError('Error approving document:', error);
      throw new Error('Failed to approve document: ' + error.message);
    }
  }

  /**
   * Reject a document
   * @param {string} documentId - Document ID
   * @param {string} reviewerId - QA reviewer user ID
   * @param {string} reason - Rejection reason
   * @param {string} notes - Additional notes
   * @returns {Promise<Object>} Result
   */
  async rejectDocument(documentId, reviewerId, reason, notes = '') {
    try {
      // Handle both full path and document ID
      let docRef;
      if (documentId.includes('/')) {
        docRef = doc(db, documentId);
      } else {
        docRef = doc(db, this.documentsCollection, documentId);
      }

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('Document not found');
      }
      const docData = docSnap.data();

      const reviewUpdate = {
        reviewStatus: 'qa_rejected',
        status: 'rejected',
        reviewedBy: reviewerId,
        reviewedAt: serverTimestamp(),
        rejectionReason: reason,
        reviewNotes: notes,
        flaggedForReview: false
      };

      // Update document status
      await updateDoc(docRef, reviewUpdate);
      await this._syncLearnerDocumentMirrorStatus(docRef, docData, reviewUpdate);

      // Create review record
      await addDoc(collection(db, this.qaReviewsCollection), {
        documentId,
        reviewerId,
        action: 'rejected',
        reason,
        notes,
        timestamp: serverTimestamp()
      });

      // Log to audit trail
      await addDoc(collection(db, this.auditCollection), {
        documentId,
        action: 'qa_rejected',
        reviewedBy: reviewerId,
        reason,
        notes,
        timestamp: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      devError('Error rejecting document:', error);
      throw new Error('Failed to reject document: ' + error.message);
    }
  }

  /**
   * Mark a document as missing/outstanding (soft delete) with a reason/comment
   * @param {string} documentId - Document ID or full path
   * @param {string} reviewerId - QA reviewer user ID
   * @param {string} reason - Reason for marking missing
   * @param {string} notes - Additional notes
   * @returns {Promise<Object>} Result
   */
  async markDocumentMissing(documentId, reviewerId, reason, notes = '') {
    try {
      let docRef;
      if (documentId.includes('/')) {
        docRef = doc(db, documentId);
      } else {
        docRef = doc(db, this.documentsCollection, documentId);
      }

      await updateDoc(docRef, {
        status: 'missing',
        reviewStatus: 'qa_rejected',
        rejectionReason: reason || 'Marked as missing',
        reviewNotes: notes || '',
        reviewedBy: reviewerId,
        reviewedAt: serverTimestamp(),
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: reviewerId
      });

      await addDoc(collection(db, this.qaReviewsCollection), {
        documentId,
        reviewerId,
        action: 'marked_missing',
        reason: reason || 'Marked as missing',
        notes,
        timestamp: serverTimestamp()
      });

      await addDoc(collection(db, this.auditCollection), {
        documentId,
        action: 'marked_missing',
        reviewedBy: reviewerId,
        reason: reason || 'Marked as missing',
        notes,
        timestamp: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      devError('Error marking document missing:', error);
      throw new Error('Failed to mark document missing: ' + error.message);
    }
  }

  /**
   * Get review history for a document
   * @param {string} documentId - Document ID
   * @returns {Promise<Array>} Review history
   */
  async getDocumentReviewHistory(documentId) {
    try {
      const reviewsQuery = query(
        collection(db, this.qaReviewsCollection),
        where('documentId', '==', documentId),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(reviewsQuery);
      const reviews = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();

        // Get reviewer info
        let reviewerInfo = null;
        if (data.reviewerId) {
          const userDoc = await getDoc(doc(db, 'users', data.reviewerId));
          if (userDoc.exists()) {
            reviewerInfo = {
              id: userDoc.id,
              displayName: userDoc.data().displayName,
              email: userDoc.data().email
            };
          }
        }

        reviews.push({
          id: docSnap.id,
          ...data,
          reviewerInfo,
          timestamp: data.timestamp?.toDate?.() || new Date(data.timestamp || Date.now())
        });
      }

      return reviews;
    } catch (error) {
      devError('Error fetching review history:', error);
      return [];
    }
  }

  /**
   * Generate QA validation statistics
   * @param {Object} dateRange - Date range for stats
   * @returns {Promise<Object>} Validation statistics
   */
  async getValidationStats(dateRange = {}) {
    try {
      const { startDate, endDate } = dateRange;

      // Get all documents
      const allDocs = await this.getAllDocuments();

      // Filter by date range if provided
      let filteredDocs = allDocs;
      if (startDate || endDate) {
        filteredDocs = allDocs.filter(doc => {
          const docDate = doc.uploadedAt;
          if (startDate && docDate < new Date(startDate)) return false;
          if (endDate && docDate > new Date(endDate)) return false;
          return true;
        });
      }

      // Calculate stats
      const stats = {
        total: filteredDocs.length,
        byValidationStatus: {
          valid: filteredDocs.filter(d => d.validationStatus === 'valid').length,
          invalid: filteredDocs.filter(d => d.validationStatus === 'invalid').length,
          warning: filteredDocs.filter(d => d.validationStatus === 'warning').length,
          pending: filteredDocs.filter(d => !d.validationStatus || d.validationStatus === 'pending').length
        },
        byReviewStatus: {
          pending_review: filteredDocs.filter(d => d.reviewStatus === 'pending_review').length,
          qa_approved: filteredDocs.filter(d => d.reviewStatus === 'qa_approved').length,
          qa_rejected: filteredDocs.filter(d => d.reviewStatus === 'qa_rejected').length,
          not_reviewed: filteredDocs.filter(d => !d.reviewStatus).length
        },
        flagged: filteredDocs.filter(d => d.flaggedForReview === true).length
      };

      return stats;
    } catch (error) {
      devError('Error generating validation stats:', error);
      throw new Error('Failed to generate validation stats: ' + error.message);
    }
  }

  /**
   * Calculate average turnaround time for reviews
   * @param {Object} dateRange - Date range
   * @returns {Promise<Object>} Turnaround time stats
   */
  async getTurnaroundTimeStats(dateRange = {}) {
    try {
      const { startDate, endDate } = dateRange;

      // Get all reviewed documents
      const allDocs = await this.getAllDocuments();
      const reviewedDocs = allDocs.filter(d =>
        d.reviewStatus &&
        (d.reviewStatus === 'qa_approved' || d.reviewStatus === 'qa_rejected') &&
        d.reviewedAt && d.flaggedAt
      );

      // Filter by date range
      let filteredDocs = reviewedDocs;
      if (startDate || endDate) {
        filteredDocs = reviewedDocs.filter(doc => {
          const reviewDate = doc.reviewedAt?.toDate?.() || new Date(doc.reviewedAt);
          if (startDate && reviewDate < new Date(startDate)) return false;
          if (endDate && reviewDate > new Date(endDate)) return false;
          return true;
        });
      }

      if (filteredDocs.length === 0) {
        return {
          average: 0,
          median: 0,
          fastest: 0,
          slowest: 0,
          total: 0
        };
      }

      // Calculate turnaround times (in hours)
      const turnaroundTimes = filteredDocs.map(doc => {
        const flaggedDate = doc.flaggedAt?.toDate?.() || new Date(doc.flaggedAt);
        const reviewDate = doc.reviewedAt?.toDate?.() || new Date(doc.reviewedAt);
        return (reviewDate - flaggedDate) / (1000 * 60 * 60); // Convert to hours
      });

      turnaroundTimes.sort((a, b) => a - b);

      const stats = {
        average: turnaroundTimes.reduce((a, b) => a + b, 0) / turnaroundTimes.length,
        median: turnaroundTimes[Math.floor(turnaroundTimes.length / 2)],
        fastest: turnaroundTimes[0],
        slowest: turnaroundTimes[turnaroundTimes.length - 1],
        total: filteredDocs.length
      };

      return stats;
    } catch (error) {
      devError('Error calculating turnaround time:', error);
      throw new Error('Failed to calculate turnaround time: ' + error.message);
    }
  }

  /**
   * Get rejection reasons statistics
   * @param {Object} dateRange - Date range
   * @returns {Promise<Array>} Rejection reasons with counts
   */
  async getRejectionReasonsStats(dateRange = {}) {
    try {
      const { startDate, endDate } = dateRange;

      // Get all rejected documents
      const allDocs = await this.getAllDocuments();
      let rejectedDocs = allDocs.filter(d => d.reviewStatus === 'qa_rejected');

      // Filter by date range
      if (startDate || endDate) {
        rejectedDocs = rejectedDocs.filter(doc => {
          const reviewDate = doc.reviewedAt?.toDate?.() || new Date(doc.reviewedAt);
          if (startDate && reviewDate < new Date(startDate)) return false;
          if (endDate && reviewDate > new Date(endDate)) return false;
          return true;
        });
      }

      // Count rejection reasons
      const reasonCounts = {};
      rejectedDocs.forEach(doc => {
        const reason = doc.rejectionReason || 'No reason provided';
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
      });

      // Convert to array and sort by count
      const reasonsArray = Object.entries(reasonCounts).map(([reason, count]) => ({
        reason,
        count,
        percentage: (count / rejectedDocs.length * 100).toFixed(1)
      }));

      reasonsArray.sort((a, b) => b.count - a.count);

      return {
        reasons: reasonsArray,
        totalRejected: rejectedDocs.length
      };
    } catch (error) {
      devError('Error getting rejection reasons:', error);
      throw new Error('Failed to get rejection reasons: ' + error.message);
    }
  }

  /**
   * Generate comprehensive QA report
   * @param {Object} dateRange - Date range for report
   * @returns {Promise<Object>} Complete QA report
   */
  async generateQAReport(dateRange = {}) {
    try {
      const [validationStats, turnaroundStats, rejectionStats] = await Promise.all([
        this.getValidationStats(dateRange),
        this.getTurnaroundTimeStats(dateRange),
        this.getRejectionReasonsStats(dateRange)
      ]);

      return {
        generatedAt: new Date(),
        dateRange,
        validation: validationStats,
        turnaround: turnaroundStats,
        rejections: rejectionStats
      };
    } catch (error) {
      devError('Error generating QA report:', error);
      throw new Error('Failed to generate QA report: ' + error.message);
    }
  }
}

// Export singleton instance
const qaService = new QAService();
export default qaService;
